import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  LuChevronDown,
  LuChevronLeft,
  LuChevronRight,
  LuX,
  LuEye,
  LuSearch,
} from "react-icons/lu";
import adminOrderController from "../controllers/adminOrderController";
import ReusableDatePicker from "../components/ReusableDatePicker";
import { userService } from "../services/userService";
import "../styles/SystemOrdersView.css";
import { translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";


const STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Payment Pending", value: "PAYMENT_PENDING" },
  { label: "Requested", value: "REQUESTED" },
  { label: "Preparing", value: "PREPARING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Canceled", value: "CANCELLED" },
];

const getStatusClass = (status) =>
  String(status || "")
    .trim()
    .replace(/_/g, "-")
    .toLowerCase();

const getStatusLabel = (status) => {
  const normalized = String(status || "")
    .trim()
    .toUpperCase();
  const match = STATUS_OPTIONS.find((option) => option.value === normalized);
  if (match) return match.label;
  if (!normalized) return "-";

  return normalized
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
};

const unwrapPayload = (response) =>
  response?.data?.data ??
  response?.data?.result ??
  response?.data ??
  response ??
  {};

const getResponseStatus = (response) =>
  response?.status ?? response?.response?.status ?? 0;

const getResponseData = (response) =>
  response?.data ?? response?.response?.data ?? {};

const unwrapOrders = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getPageTotal = (payload) =>
  Number(
    payload?.totalElements ?? payload?.total ?? payload?.numberOfElements ?? 0,
  );

const parseDateValue = (value) => {
  if (!value) return null;
  const date = value?.time ? new Date(value.time) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatApiDate = (value) => value || "";

const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "$0.00";
  return `$${number.toFixed(2)}`;
};

const formatDisplayDate = (value) => {
  const date = parseDateValue(value);
  if (!date) return "-";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDisplayTime = (value) => {
  const date = parseDateValue(value);
  if (!date) return "-";

  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const mapOrder = (order = {}) => ({
  raw: order,
  acceptedAt: order.acceptedAt,
  arrivedAt: order.arrivedAt,
  cancelledAt: order.cancelledAt,
  cancellationReason: order.cancellationReason || "",
  collectedAt: order.collectedAt,
  completedAt: order.completedAt,
  createdAt: order.createdAt,
  deliveredAt: order.deliveredAt,
  deliveryFee: Number(order.deliveryFee || 0),
  discount: Number(order.discount || 0),
  serviceFee: Number(order.serviceFee || 0),
  subtotal: Number(order.subtotal || 0),
  total: Number(order.total || 0),
  paymentMethod: order.paymentMethod || "-",
  paymentStatus: order.paymentStatus || "",
  pickupLocation: order.pickupLocation || "-",
  dropoffLocation: order.dropoffLocation || "-",
  items: Array.isArray(order.items) ? order.items : [],
  amount: formatMoney(order.total),
  customerName: order.customerName || "-",
  customerPhone: order.customerPhone || "-",
  date: formatDisplayDate(order.createdAt),
  driver: order.driverName || "-",
  driverPhone: order.driverPhone || "-",
  email: order.customerEmail || "-",
  id: order.orderNumber ? `ORD-${order.orderNumber}` : order.orderId || "-",
  orderId: order.orderId || "",
  status: order.status || "",
  statusLabel: getStatusLabel(order.status),
  time: formatDisplayTime(order.createdAt),
  vendor: order.vendorName || "-",
  vendorPhone: order.vendorPhone || "-",
});

function SystemOrdersView() {
  useTranslationSnapshot();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusOpen, setStatusOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [dateRange, setDateRange] = useState({
    from: "2026-05-01",
    to: "2026-05-31",
  });
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [page, setPage] = useState(1);

  const statusLabel =
    STATUS_OPTIONS.find((option) => option.value === statusFilter)?.label ||
    "All Status";

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const visibleOrders = useMemo(() => orders, [orders]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError("");

    try {
      const response = await adminOrderController.getOrders({
        Authorization: userService.getJwt(),
        fromDate: formatApiDate(dateRange.from),
        page: Math.max(page - 1, 0),
        search: search.trim(),
        size: rowsPerPage,
        status: statusFilter,
        toDate: formatApiDate(dateRange.to),
      });
      const responseStatus = getResponseStatus(response);

      if (responseStatus >= 400) {
        const responseData = getResponseData(response);
        console.error("[Admin orders] request failed", {
          status: responseStatus,
          data: responseData,
        });
        throw new Error(
          responseData?.message ||
            responseData?.error ||
            `Orders request failed with status ${responseStatus}`,
        );
      }

      const payload = unwrapPayload(response);
      const nextOrders = unwrapOrders(payload).map(mapOrder);

      setOrders(nextOrders);
      setTotalRows(getPageTotal(payload) || nextOrders.length);
    } catch (error) {
      console.error("[Admin orders] list failed", error);
      setOrders([]);
      setTotalRows(0);
      setOrdersError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load orders.",
      );
    } finally {
      setOrdersLoading(false);
    }
  }, [dateRange.from, dateRange.to, page, rowsPerPage, search, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const updateRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const closeReceipt = () => setSelectedOrder(null);

  const selectedStatus = String(selectedOrder?.status || "").toUpperCase();
  const selectedIsCanceled =
    selectedStatus === "CANCELLED" ||
    selectedStatus === "CANCELED" ||
    selectedStatus === "REJECTED";
  const selectedIsPending =
    selectedStatus === "REQUESTED" ||
    selectedStatus === "PREPARING" ||
    selectedStatus === "PAYMENT_PENDING";
  const selectedIsCompleted = selectedStatus === "COMPLETED";
  const selectedItems =
    selectedOrder?.items?.length > 0
      ? selectedOrder.items.map((item, index) => ({
          quantity: `x${item.quantity || 1}`,
          name:
            item.itemName ||
            item.name ||
            item.selectedPortion ||
            `Item ${index + 1}`,
          price: formatMoney(item.price),
        }))
      : [];
  const selectedTotals = selectedOrder
    ? [
        ["Subtotal", formatMoney(selectedOrder.subtotal)],
        ["Delivery Fee", formatMoney(selectedOrder.deliveryFee)],
        ["Discount", `-${formatMoney(selectedOrder.discount)}`],
        ["Total", formatMoney(selectedOrder.total)],
      ]
    : [];

  return (
    <div className="system-orders-page">
      <div className="system-orders-header">
        <div className="system-orders-heading">
          <div className="system-orders-back">
            <LuChevronLeft className="navigation-direction-icon" aria-hidden="true" />
          </div>
          <div>
            <div className="system-orders-title">{translateLabel("txt_end_user_orders", "Orders")}</div>
            <div className="system-orders-subtitle">
              View, manage, and track all customer orders in one place.
            </div>
          </div>
        </div>
        <div className="system-orders-date-pickers">
          <ReusableDatePicker
            ariaLabel="Orders from date"
            className="system-orders-date-picker"
            value={dateRange.from}
            onChange={(value) => {
              setDateRange((current) => ({ ...current, from: value }));
              setPage(1);
            }}
          />
          <ReusableDatePicker
            ariaLabel="Orders to date"
            className="system-orders-date-picker"
            value={dateRange.to}
            onChange={(value) => {
              setDateRange((current) => ({ ...current, to: value }));
              setPage(1);
            }}
          />
        </div>
      </div>

      <div className="system-orders-filters">
        <div className="system-orders-search">
          <LuSearch aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={translateLabel("txt_admin_search_by_order_id_customer_name_date_or_email", "Search by order ID, customer name, date or email...")}
          />
        </div>

        <div className="system-orders-status-filter">
          <button
            className={`system-orders-status-trigger ${statusOpen ? "open" : ""}`}
            type="button"
            onClick={() => setStatusOpen((current) => !current)}
          >
            <span>{translateText(statusLabel)}</span>
            <LuChevronDown aria-hidden="true" />
          </button>

          {statusOpen && (
            <div className="system-orders-status-menu">
              {STATUS_OPTIONS.map((status) => (
                <button
                  className={status.value === statusFilter ? "active" : ""}
                  key={status.value || "all"}
                  type="button"
                  onClick={() => {
                    setStatusFilter(status.value);
                    setPage(1);
                    setStatusOpen(false);
                  }}
                >
                  {translateText(status.label)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="system-orders-table-shell">
        <div className="system-orders-table">
          <div className="system-orders-row system-orders-row-head">
            <div className="system-orders-cell system-orders-checkbox-cell">
              <input type="checkbox" aria-label={translateLabel("txt_admin_select_all_orders", "Select all orders")} />
            </div>
            <div className="system-orders-cell">{translateLabel("txt_end_user_order_id", "Order ID")}</div>
            <div className="system-orders-cell">{translateLabel("txt_common_customer_name", "Customer Name")}</div>
            <div className="system-orders-cell">{translateLabel("txt_end_user_email", "Email")}</div>
            <div className="system-orders-cell">Date</div>
            <div className="system-orders-cell">{translateLabel("txt_admin_amount", "Amount")}</div>
            <div className="system-orders-cell">{translateLabel("txt_common_status", "Status")}</div>
            <div className="system-orders-cell">Assigned Vendor</div>
            <div className="system-orders-cell">Assigned Driver</div>
            <div className="system-orders-cell system-orders-action-cell"> {translateLabel("txt_common_actions", "Actions")} </div>
          </div>

          {visibleOrders.map((order) => (
            <div className="system-orders-row" key={order.id}>
              <div className="system-orders-cell system-orders-checkbox-cell">
                <input type="checkbox" aria-label={`Select ${order.id}`} />
              </div>
              <div className="system-orders-cell system-orders-order-id">
                {order.id}
              </div>
              <div className="system-orders-cell">
                <div className="system-orders-main-text">
                  {order.customerName}
                </div>
                <div className="system-orders-muted-text">
                  {order.customerPhone}
                </div>
              </div>
              <div className="system-orders-cell system-orders-email-cell">
                {order.email}
              </div>
              <div className="system-orders-cell">
                <div className="system-orders-main-text">{order.date}</div>
                <div className="system-orders-muted-text">{order.time}</div>
              </div>
              <div className="system-orders-cell system-orders-amount-cell">
                {order.amount}
              </div>
              <div className="system-orders-cell">
                <span
                  className={`system-orders-status-pill ${getStatusClass(order.status)}`}
                >
                  {order.statusLabel}
                </span>
              </div>
              <div className="system-orders-cell">
                <div className="system-orders-main-text">{order.vendor}</div>
                <div className="system-orders-muted-text">
                  {order.vendorPhone}
                </div>
              </div>
              <div className="system-orders-cell">
                <div className="system-orders-main-text">{order.driver}</div>
                <div className="system-orders-muted-text">
                  {order.driverPhone}
                </div>
              </div>
              <div className="system-orders-cell system-orders-action-cell">
                <button
                  className="system-orders-eye-button"
                  type="button"
                  aria-label={`View ${order.id}`}
                  onClick={() => setSelectedOrder(order)}
                >
                  <LuEye aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}

          {(ordersLoading || ordersError || visibleOrders.length === 0) && (
            <div className="system-orders-empty">
              {ordersLoading
                ? translateLabel("txt_admin_loading_orders", "Loading orders...")
                : ordersError || translateLabel("txt_admin_no_orders_found", "No orders found.")}
            </div>
          )}
        </div>
      </div>

      <div className="system-orders-pagination">
        <div className="system-orders-page-size">
          <span>{translateLabel("txt_common_rows_per_page", "Rows per page")}</span>
          <select value={rowsPerPage} onChange={updateRowsPerPage}>
            <option value={9}>9</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
          </select>
          <span>of {totalRows} rows</span>
        </div>

        <div className="system-orders-page-controls">
          <button
            className="system-orders-page-control"
            type="button"
            disabled={safePage <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            <LuChevronLeft className="navigation-direction-icon" aria-hidden="true" />
          </button>
          {[1, 2, 3]
            .filter((pageNumber) => pageNumber <= totalPages)
            .map((pageNumber) => (
              <button
                className={`system-orders-page-number ${pageNumber === safePage ? "active" : ""}`}
                key={pageNumber}
                type="button"
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
          <span>...</span>
          <button
            className={`system-orders-page-number ${safePage === totalPages ? "active" : ""}`}
            type="button"
            onClick={() => setPage(totalPages)}
          >
            {totalPages}
          </button>
          <button
            className="system-orders-page-control"
            type="button"
            disabled={safePage >= totalPages}
            onClick={() =>
              setPage((current) => Math.min(totalPages, current + 1))
            }
          >
            <LuChevronRight className="navigation-direction-icon" aria-hidden="true" />
          </button>
        </div>
      </div>

      {selectedOrder && (
        <div className="system-orders-modal-backdrop">
          <div
            className={`system-orders-receipt-modal ${getStatusClass(selectedOrder.status)}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedOrder.id} receipt`}
          >
            <button
              className="system-orders-modal-close"
              type="button"
              aria-label={translateLabel("txt_admin_close_receipt", "Close receipt")}
              onClick={closeReceipt}
            >
              <LuX aria-hidden="true" />
            </button>

            <div className="system-orders-receipt-top">
              <div>
                <div className="system-orders-receipt-title">{translateLabel("txt_end_user_order_receipt", "Order Receipt")}</div>
                <div className="system-orders-receipt-id">
                  {selectedOrder.id}
                </div>
                <div className="system-orders-receipt-muted">
                  {selectedOrder.date} - {selectedOrder.time}
                </div>
              </div>
              <span
                className={`system-orders-status-pill ${getStatusClass(selectedOrder.status)}`}
              >
                {selectedOrder.statusLabel}
              </span>
            </div>

            <div className="system-orders-receipt-parties">
              <div className="system-orders-party-card">
                <div className="system-orders-receipt-label">{translateLabel("txt_common_customer", "Customer")}</div>
                <div className="system-orders-receipt-strong">
                  {selectedOrder.customerName}
                </div>
                <div className="system-orders-receipt-muted">
                  {selectedOrder.customerPhone}
                </div>
                <div className="system-orders-receipt-muted">
                  {selectedOrder.dropoffLocation}
                </div>
              </div>
              <div className="system-orders-party-card">
                <div className="system-orders-receipt-label">{translateLabel("txt_admin_from", "From")}</div>
                <div className="system-orders-receipt-strong">
                  {selectedOrder.vendor}
                </div>
                <div className="system-orders-receipt-muted">
                  {selectedOrder.pickupLocation}
                </div>
              </div>
              <div className="system-orders-party-card">
                <div className="system-orders-receipt-label">{translateLabel("txt_time_to", "To")}</div>
                <div className="system-orders-receipt-strong">
                  {selectedOrder.customerName}
                </div>
                <div className="system-orders-receipt-muted">
                  {selectedOrder.dropoffLocation}
                </div>
              </div>
            </div>

            <div className="system-orders-receipt-grid">
              <div className="system-orders-receipt-card system-orders-order-details">
                <div className="system-orders-receipt-card-title"> {translateLabel("txt_common_order_details", "Order Details")} </div>
                {selectedItems.map((item) => (
                  <div className="system-orders-receipt-line" key={item.name}>
                    <div className="system-orders-receipt-item">
                      <span>{item.quantity}</span>
                      <span>{item.name}</span>
                    </div>
                    <div>{item.price}</div>
                  </div>
                ))}
                <div className="system-orders-receipt-divider" />
                {selectedTotals.map(([label, value]) => (
                  <div className="system-orders-receipt-total-line" key={label}>
                    <div>{translateText(label)}</div>
                    <div>{value}</div>
                  </div>
                ))}
              </div>

              <div className="system-orders-receipt-side">
                <div className="system-orders-receipt-card">
                  <div className="system-orders-receipt-card-title"> {translateLabel("txt_end_user_payment_method", "Payment Method")} </div>
                  <div className="system-orders-receipt-strong">
                    Paid by {selectedOrder.paymentMethod}
                  </div>
                </div>

                {selectedIsCanceled && (
                  <div className="system-orders-receipt-card system-orders-cancel-card">
                    <div className="system-orders-receipt-card-title">
                      Cancellation Details
                    </div>
                    <div className="system-orders-cancel-row">
                      <div className="system-orders-cancel-icon">!</div>
                      <div className="system-orders-cancel-copy">
                        <div className="system-orders-receipt-strong">
                          Canceled on
                        </div>
                        <div className="system-orders-receipt-muted">
                          {formatDisplayDate(
                            selectedOrder.cancelledAt ||
                              selectedOrder.createdAt,
                          )}{" "}
                          -{" "}
                          {formatDisplayTime(
                            selectedOrder.cancelledAt ||
                              selectedOrder.createdAt,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="system-orders-cancel-meta">
                      <div>{translateLabel("txt_admin_canceled_by", "Canceled by")}</div>
                      <div>{selectedOrder.customerName}</div>
                      <div>{translateLabel("txt_admin_reason", "Reason")}</div>
                      <div>
                        {selectedOrder.cancellationReason || translateLabel("txt_admin_changed_my_mind", "Changed my mind")}
                      </div>
                      <div>{translateLabel("txt_admin_refund_status", "Refund status")}</div>
                      <div className="system-orders-refund-badge">
                        Refund Initiated
                      </div>
                      <div>{translateLabel("txt_admin_refund_amount", "Refund amount")}</div>
                      <div>{selectedOrder.amount}</div>
                    </div>
                  </div>
                )}

                {!selectedIsCanceled && (
                  <div className="system-orders-receipt-card">
                    <div className="system-orders-receipt-card-title">
                      Order Timeline
                    </div>
                    <div className="system-orders-timeline">
                      <div className="system-orders-timeline-item">
                        <span />
                        <div>
                          <div className="system-orders-receipt-strong">
                            Order placed
                          </div>
                          <div className="system-orders-receipt-muted">
                            {selectedOrder.date} - {selectedOrder.time}
                          </div>
                        </div>
                      </div>
                      <div className="system-orders-timeline-item">
                        <span />
                        <div>
                          <div className="system-orders-receipt-strong">
                            Picked up by driver
                          </div>
                          <div className="system-orders-receipt-muted">
                            {selectedOrder.date} - 10:30 AM
                          </div>
                        </div>
                      </div>
                      {selectedIsCompleted && (
                        <div className="system-orders-timeline-item">
                          <span />
                          <div>
                            <div className="system-orders-receipt-strong">
                              Delivered
                            </div>
                            <div className="system-orders-receipt-muted">
                              {selectedOrder.date} - 10:45 AM
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {selectedIsCanceled && (
                <div className="system-orders-receipt-card system-orders-full-timeline">
                  <div className="system-orders-receipt-card-title">
                    Order Timeline
                  </div>
                  <div className="system-orders-timeline canceled">
                    <div className="system-orders-timeline-item">
                      <span />
                      <div>
                        <div className="system-orders-receipt-strong">
                          Order placed
                        </div>
                        <div className="system-orders-receipt-muted">
                          {formatDisplayDate(selectedOrder.createdAt)} -{" "}
                          {formatDisplayTime(selectedOrder.createdAt)}
                        </div>
                      </div>
                    </div>
                    <div className="system-orders-timeline-item">
                      <span />
                      <div>
                        <div className="system-orders-receipt-strong">
                          Order confirmed
                        </div>
                        <div className="system-orders-receipt-muted">
                          {formatDisplayDate(
                            selectedOrder.acceptedAt || selectedOrder.createdAt,
                          )}{" "}
                          -{" "}
                          {formatDisplayTime(
                            selectedOrder.acceptedAt || selectedOrder.createdAt,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="system-orders-timeline-item danger">
                      <span />
                      <div>
                        <div className="system-orders-receipt-strong"> {translateLabel("txt_admin_canceled", "Canceled")} </div>
                        <div className="system-orders-receipt-muted">
                          {formatDisplayDate(
                            selectedOrder.cancelledAt ||
                              selectedOrder.createdAt,
                          )}{" "}
                          -{" "}
                          {formatDisplayTime(
                            selectedOrder.cancelledAt ||
                              selectedOrder.createdAt,
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="system-orders-receipt-actions">
              {selectedIsPending && (
                <button
                  className="system-orders-cancel-order-button"
                  type="button"
                >
                  Cancel Order
                </button>
              )}
              <button
                className="system-orders-close-button"
                type="button"
                onClick={closeReceipt}
              > {translateLabel("btn_close", "Close")} </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SystemOrdersView;
